import {
  Banner, Badge, BlockStack, Button, ButtonGroup, Checkbox, DataTable,
  InlineStack, Layout, Page, Text, TextField,
} from '../components/admin-ui';
import { useCallback, useEffect, useMemo, useState } from "react";
import { Drawer, Notice } from "../componens/adminUi";
import ShopScopeSelector from "../componens/shopScopeSelector";
import { admin_shops, admin_accounts, create_admin_account, update_admin_account } from "../api";

const groups = [
  { title: "Shop settings", permissions: ["shops.view", "shops.manage"] },
  { title: "Store performance", permissions: ["performance.view", "performance.export"] },
  { title: "Logs", permissions: ["logs.view", "logs.delete"] },
  { title: "COD risk data", permissions: ["cod_risk.import"] },
  { title: "Account administration", permissions: ["accounts.manage"] },
];

const emptyDraft = () => ({ user: "", password: "", permissions: [], grantable_permissions: [], is_delete: 0, shop_scope: "selected", shop_ids: [] });

export default function AdminAccounts({ context, permissions }) {
  const [accounts, setAccounts] = useState([]);
  const [shops, setShops] = useState([]);
  const [shopsLoading, setShopsLoading] = useState(true);
  const [shopsError, setShopsError] = useState("");
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState("");
  const owner = Boolean(context.is_owner);
  const canGrantAll = owner || context.shop_scope === "all";
  const labels = useMemo(() => context.permission_labels || {}, [context.permission_labels]);
  const grantableByMe = new Set(context.grantable_permissions || []);
  const dependencies = { "shops.manage": "shops.view", "performance.export": "performance.view", "logs.delete": "logs.view" };

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await admin_accounts({});
      if (response.status === 200) setAccounts(response.data || []);
      else setMessage({ tone: "critical", text: response.msg || "Unable to load accounts." });
    } catch (requestError) {
      setMessage({ tone: "critical", text: requestError?.response?.data?.msg || "Unable to load accounts." });
    } finally { setLoading(false); }
  }, []);

  const loadShops = useCallback(async () => {
    setShopsLoading(true); setShopsError("");
    try {
      const response = await admin_shops({});
      if (response.status !== 200 || !Array.isArray(response.data)) throw new Error(response.msg || "Unable to load available shops.");
      setShops(response.data);
    } catch (requestError) { setShopsError(requestError?.message || "Unable to load available shops."); }
    finally { setShopsLoading(false); }
  }, []);
  useEffect(() => { loadAccounts(); loadShops(); }, [loadAccounts, loadShops]);

  const openEdit = (account) => {
    setError("");
    setMessage(null);
    setDraft({ ...account, shop_scope: account.shop_scope === "all" ? "all" : "selected", shop_ids: [...(account.shop_ids || [])].map(Number), password: "", permissions: [...(account.permissions || [])], grantable_permissions: [...(account.grantable_permissions || [])] });
  };
  const toggle = (field, permission, checked) => setDraft((current) => {
    const values = new Set(current[field] || []);
    if (checked) values.add(permission); else values.delete(permission);
    if (checked && dependencies[permission]) values.add(dependencies[permission]);
    if (!checked) Object.entries(dependencies).forEach(([child, parent]) => parent === permission && values.delete(child));
    if (field === "permissions" && permission === "accounts.manage" && !checked) return { ...current, permissions: [...values], grantable_permissions: [] };
    return { ...current, [field]: [...values] };
  });

  const saveError = (response) => {
    const fields = response?.data?.errors || response?.errors;
    const details = fields && Object.values(fields).flat().filter((value) => typeof value === "string");
    return details?.length ? [...new Set(details)].join(" ") : response?.msg || response?.message || "Unable to save account. Please retry.";
  };

  const save = async () => {
    if (saving) return;
    setError("");
    if (draft.user.trim().length < 2) return setError("Enter a login name with at least 2 characters.");
    if (draft.id == null && !/^[A-Za-z0-9._@-]+$/.test(draft.user.trim())) return setError("Use letters, numbers, dots, underscores, @ or hyphens for the login name.");
    if (draft.id == null && draft.user.trim().length > 100) return setError("The login name must be at most 100 characters.");
    if ((draft.id == null || draft.password.length > 0) && draft.password.length < 12) return setError("Use a password with at least 12 characters.");
    if (draft.password.length > 200) return setError("The password must be at most 200 characters.");
    if (!owner && draft.permissions.includes("accounts.manage")) return setError("Only the system owner can authorize account administration.");
    if (!owner && draft.permissions.some((permission) => !grantableByMe.has(permission))) return setError("This account includes a permission outside your grantable access.");
    if (draft.shop_scope === "all" && !canGrantAll) return setError("You cannot grant access to all shops.");
    if (draft.shop_scope === "selected") {
      if (shopsLoading || shopsError) return setError("Load the available shops before saving.");
      if (!draft.shop_ids?.length) return setError("Select at least one shop.");
      if (draft.shop_ids.some((id) => !shops.some((shop) => Number(shop.id) === Number(id)))) return setError("Remove unavailable shops before saving.");
    }
    setSaving(true);
    try {
      const payload = { ...draft, user: draft.user.trim(), shop_ids: draft.shop_scope === "all" ? [] : draft.shop_ids.map(Number) };
      const response = draft.id == null ? await create_admin_account(payload) : await update_admin_account(payload);
      if (response.status !== 200) return setError(saveError(response));
      setDraft(null);
      setMessage({ tone: "success", text: draft.id == null ? "Account created." : "Account permissions updated." });
      await loadAccounts();
    } catch (requestError) {
      setError(requestError?.response?.data ? saveError(requestError.response.data) : "Unable to reach the server. Check your connection and retry.");
    } finally { setSaving(false); }
  };

  const rows = useMemo(() => accounts.map((account) => [
    account.user,
    <Badge tone={account.is_delete ? "critical" : "success"}>{account.is_delete ? "Disabled" : "Active"}</Badge>,
    account.is_owner ? "System owner" : (account.permissions || []).map((permission) => labels[permission] || permission).join(", ") || "No features authorized",
    <Button variant="plain" onClick={() => openEdit(account)} disabled={account.is_owner || account.id === context.admin.id}>{account.shop_scope === "all" || account.is_owner ? "All shops" : `Selected ${(account.shop_ids || []).length} ${(account.shop_ids || []).length === 1 ? "shop" : "shops"}`}</Button>,
    <Button onClick={() => openEdit(account)} disabled={account.is_owner || account.id === context.admin.id}>Edit access</Button>,
  ]), [accounts, context.admin.id, labels]);

  return (
    <Page title="Accounts & permissions" subtitle="Choose each account's features and shop access independently.">
      <Layout>
        {message && <Layout.Section><Banner tone={message.tone} onDismiss={() => setMessage(null)}>{message.text}</Banner></Layout.Section>}
        <Layout.Section>
          <InlineStack align="space-between" blockAlign="center">
            <Text as="p" tone="subdued">A/B shop links are visible to the system owner only.</Text>
            <Button variant="primary" onClick={() => { setError(""); setMessage(null); setDraft(emptyDraft()); }}>Create account</Button>
          </InlineStack>
        </Layout.Section>
        <Layout.Section>
          <DataTable columnContentTypes={["text", "text", "text", "text", "text"]} headings={["Login", "Status", "Authorized features", "Shop access", ""]} rows={rows} loading={loading} />
        </Layout.Section>
      </Layout>

      {draft && <div className="admin-account-editor"><Drawer
        title={draft.id == null ? "Create admin account" : "Edit access - " + draft.user}
        onClose={() => setDraft(null)} busy={saving}
        footer={<div className="admin-account-save-footer">
          <Notice>{error}</Notice>
          <InlineStack align="end"><ButtonGroup><Button onClick={() => setDraft(null)} disabled={saving}>Cancel</Button><Button variant="primary" onClick={save} loading={saving} disabled={saving}>Save</Button></ButtonGroup></InlineStack>
        </div>}>
          <fieldset disabled={saving}><BlockStack gap="400">
            <TextField label="Login name" value={draft.user} onChange={(user) => setDraft((current) => ({ ...current, user }))} autoComplete="username" disabled={saving || draft.id != null} helpText={draft.id == null ? "2-100 characters: letters, numbers, dots, underscores, @ or hyphens." : undefined} />
            {draft.id == null && <TextField label="Initial password" type="password" disabled={saving} value={draft.password} onChange={(password) => setDraft((current) => ({ ...current, password }))} autoComplete="new-password" minLength={12} helpText="At least 12 characters." />}
            {draft.id != null && <TextField label="Reset password (optional)" type="password" disabled={saving} value={draft.password} onChange={(password) => setDraft((current) => ({ ...current, password }))} autoComplete="new-password" minLength={12} helpText="Leave blank to keep the current password. A new password revokes existing sessions." />}
            <ShopScopeSelector value={draft} shops={shops} canGrantAll={canGrantAll} loading={shopsLoading} error={shopsError} onRetry={loadShops} disabled={saving}
              onChange={(scope) => { setError(""); setDraft((current) => ({ ...current, ...scope })); }} />
            <Text as="p" tone="subdued">Shop features apply within the selected shop access. COD credit import and review grants access to all application import batches, evidence records and sharing decisions, independently of shop access. Exchange exports remain restricted to the system owner. Changing access requires this account to sign in again.</Text>
            {groups.map((group) => <div key={group.title} className="admin-account-permission-group"><Text as="h3" variant="headingSm">{group.title}</Text>{group.permissions.map((permission) => <Checkbox key={permission} label={labels[permission] || permission} checked={(draft.permissions || []).includes(permission)} disabled={saving || (!owner && (permission === "accounts.manage" || !grantableByMe.has(permission)))} onChange={(checked) => toggle("permissions", permission, checked)} />)}</div>)}
            {owner && draft.permissions.includes("accounts.manage") && <div className="admin-account-permission-group"><Text as="h3" variant="headingSm">Permissions this account can grant</Text><Text as="p" tone="subdued">It can only grant business permissions assigned to its own account.</Text>{permissions.filter((permission) => permission !== "accounts.manage").map((permission) => <Checkbox key={permission} label={labels[permission] || permission} checked={(draft.grantable_permissions || []).includes(permission)} disabled={saving || !(draft.permissions || []).includes(permission)} onChange={(checked) => toggle("grantable_permissions", permission, checked)} />)}</div>}
            {draft.id != null && <Checkbox disabled={saving} label="Account enabled" checked={!draft.is_delete} onChange={(checked) => setDraft((current) => ({ ...current, is_delete: checked ? 0 : 1 }))} />}
          </BlockStack></fieldset>
      </Drawer></div>}
    </Page>
  );
}
